import { CheckCircle2, Clock3, MessageSquareLock, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const formatFreshness = (value) => {
  if (!value) return "Freshness updates are syncing";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Freshness updates are syncing";
  const diffMinutes = Math.max(0, Math.round((Date.now() - parsed.getTime()) / 60000));
  if (diffMinutes <= 1) return "Updated just now";
  if (diffMinutes < 60) return `Updated ${diffMinutes} mins ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours}h ago`;
  return `Updated ${Math.round(diffHours / 24)}d ago`;
};

export const AgentTrustPanel = ({ branding, buildBrandedPath, compact = false, listingCount = 0, lastUpdatedAt = null, testIdPrefix = "agent-trust", variant = "trust", onExploreNow }) => {
  const trustCards = [
    {
      icon: ShieldCheck,
      label: "Verified identity",
      value: branding?.verified_badge_label || (branding?.cea_registration_number ? `CEA ${branding.cea_registration_number}` : "Verified SG Prop Portal route"),
      detail: "Identity, contact path, and advisor ownership are visible before buyers enquire.",
    },
    {
      icon: Clock3,
      label: "Live inventory integrity",
      value: listingCount ? `${listingCount} live listings` : "Live portfolio",
      detail: formatFreshness(lastUpdatedAt),
    },
    {
      icon: MessageSquareLock,
      label: "Guided response rail",
      value: branding?.response_sla || "Shortlist → WhatsApp → Viewing",
      detail: "One workflow for pricing clarity, shortlist refinement, and private viewing coordination.",
    },
    {
      icon: Sparkles,
      label: "Human + AI coverage",
      value: "Advisor + Signature AI",
      detail: "Instant first response with a human handoff still visible on the same route.",
    },
  ];

  const signatureAdvisorFeatures = [
    "Remembers your district and budget preferences",
    "Compares shortlisted homes side by side",
    "Highlights the strongest-fit units quickly",
    "Explains investor upside and risk clearly",
    "Summarises district strengths in plain language",
    "Answers common buyer objections instantly",
    "Prepares a clear investment memo on demand",
    "Shows scenario outcomes before you commit",
    "Prepares a smoother handoff to a real advisor",
    "Lets you continue on WhatsApp without repeating yourself",
  ];

  if (variant === "signature-advisor") {
    return (
      <section className="rounded-[32px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-5 shadow-[0_18px_50px_rgba(90,75,53,0.08)] md:p-6" data-testid={`${testIdPrefix}-panel`}>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8B6E3D]">Quick property help</p>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid={`${testIdPrefix}-heading`}>
            Need help while browsing this home?
          </h2>
          <p className="mt-3 text-sm leading-7 text-[#5D584F]" data-testid={`${testIdPrefix}-copy`}>
            Ask quick questions, compare nearby options, and move to WhatsApp without repeating yourself.
          </p>
        </div>

        <div className="mt-5 grid gap-3" data-testid={`${testIdPrefix}-features-list`}>
          {signatureAdvisorFeatures.map((feature, index) => (
            <div className="rounded-[22px] border border-[#D4AF37]/16 bg-white/82 px-4 py-3" data-testid={`${testIdPrefix}-feature-${index + 1}`} key={feature}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
                <p className="text-sm font-medium leading-6 text-[#2C2C2C]">{feature}</p>
              </div>
            </div>
          ))}
        </div>

        <button className="gold-button mt-5 w-full !justify-center" data-testid={`${testIdPrefix}-explore-now-button`} onClick={onExploreNow} type="button">
          Get instant help
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-[32px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-5 shadow-[0_18px_50px_rgba(90,75,53,0.08)] md:p-6" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#8B6E3D]">Why clients trust this portal</p>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid={`${testIdPrefix}-heading`}>
            Buyers can see who they are speaking with before they enquire.
          </h2>
          <p className="mt-3 text-sm leading-7 text-[#5D584F]" data-testid={`${testIdPrefix}-copy`}>
            Verified identity, live listings, and a clear contact path make it easier to shortlist, ask questions, and arrange a viewing.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a className="gold-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-contact-button`} href={`mailto:${branding?.email || "hello@sgpropportal.sg"}`}>
            Contact team
          </a>
          <Link className="outline-button !px-4 !py-2 !text-xs !tracking-[0.16em]" data-testid={`${testIdPrefix}-listings-button`} to={buildBrandedPath("/listings")}>
            View listings
          </Link>
        </div>
      </div>

      <div className={`mt-5 grid gap-4 ${compact ? "md:grid-cols-2" : "xl:grid-cols-4 md:grid-cols-2"}`} data-testid={`${testIdPrefix}-cards-grid`}>
        {trustCards.map((card) => {
          const Icon = card.icon;
          return (
            <div className="rounded-[24px] border border-[#D4AF37]/16 bg-white/80 p-5" data-testid={`${testIdPrefix}-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={card.label}>
              <div className="flex items-center gap-3 text-[#8B6E3D]">
                <div className="rounded-full border border-[#D4AF37]/24 bg-[#fff4db] p-3"><Icon className="h-4 w-4" /></div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em]">{card.label}</p>
              </div>
              <p className="mt-4 text-xl font-semibold text-[#2C2C2C]" data-testid={`${testIdPrefix}-value-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{card.value}</p>
              <p className="mt-3 text-sm leading-7 text-[#5D584F]">{card.detail}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};